import { useTranslation } from "react-i18next"
import { Box, Divider, Skeleton, Stack, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles"

import EntityLink from "./EntityLink"
import OverflowChip, { OverflowItem } from "./OverflowChip"

export interface PowerLineRef {
  id: number
  name: string | null
  /** The line's spans at the asset, listed under its name. */
  spans?: { id: number; name: string }[]
}

/** Ties a line's spans to the line's icon above them. */
const Connector = () => (
  <Box
    sx={(theme) => ({
      flexShrink: 0,
      alignSelf: "flex-start",
      width: "8px",
      height: "14px",
      marginTop: "-5px",
      marginLeft: "5px",
      marginRight: "6px",
      borderLeft: `1px solid ${theme.palette.neutral[500]}`,
      borderBottom: `1px solid ${theme.palette.neutral[500]}`,
    })}
  />
)

interface PowerLineColumnProps {
  line: PowerLineRef
  spansLoading?: boolean
}

const PowerLineColumn = ({ line, spansLoading }: PowerLineColumnProps) => (
  <Stack gap={0.5} minWidth={0}>
    <EntityLink entity="powerline" id={line.id} name={line.name} maxLines={1} />
    {line.spans && (spansLoading || line.spans.length > 0) && (
      <Stack direction="row" alignItems="center" minWidth={0}>
        <Connector />
        {spansLoading ? (
          <Skeleton width={120} />
        ) : (
          <Stack direction="row" columnGap={2} minWidth={0}>
            {line.spans.map((span) => (
              <EntityLink
                key={span.id}
                entity="span"
                id={span.id}
                name={span.name}
                maxLines={1}
              />
            ))}
          </Stack>
        )}
      </Stack>
    )}
  </Stack>
)

interface PowerLineColumnsProps {
  lines: PowerLineRef[]
  /** The lines are known and their spans are not yet: each gets a placeholder under it. */
  spansLoading?: boolean
}

/**
 * Each line as a column, its spans under it. Two slots at most so the cell never
 * grows: with more lines the second slot is a +N with the rest.
 */
const PowerLineColumns = ({ lines, spansLoading }: PowerLineColumnsProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  // Stacked on mobile, so two lines fit before the +N.
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const visibleCount = lines.length <= 2 ? lines.length : isMobile ? 2 : 1
  const hidden = lines.slice(visibleCount)

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "flex-start", md: "stretch" }}
      spacing={{ xs: 1, md: 2 }}
      divider={
        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", md: "block" } }}
        />
      }
      minWidth={0}
    >
      {lines.slice(0, visibleCount).map((line) => (
        <PowerLineColumn key={line.id} line={line} spansLoading={spansLoading} />
      ))}
      {hidden.length > 0 && (
        <Box display="flex" alignItems="center" flexShrink={0}>
          <OverflowChip
            count={hidden.length}
            ariaLabel={t("asset_context.MORE_POWERLINES", { count: hidden.length })}
          >
            {hidden.map((line) => (
              <OverflowItem key={line.id}>
                <PowerLineColumn line={line} spansLoading={spansLoading} />
              </OverflowItem>
            ))}
          </OverflowChip>
        </Box>
      )}
    </Stack>
  )
}

export default PowerLineColumns
